/**
 * Tab Accordion custom element that manages tab navigation and panel visibility.
 *
 * @extends {HTMLElement}
 */
class TabAccordion extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll('.tab-accordion__tab'));
  }

  /** @type {HTMLElement[]} */
  get panels() {
    return Array.from(this.querySelectorAll('.tab-accordion__panel'));
  }

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#initTabs();

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.#handleTabClick, { signal });
      tab.addEventListener('keydown', this.#handleKeydown, { signal });
    });

    this.addEventListener('refresh-tabs', this.#initTabs.bind(this), { signal });
  }

  #initTabs() {
    this.tabs.forEach((tab) => {
      const tabLabel = tab.querySelector('.tab-accordion__tab-label');
      const id = tab.dataset.tabId;
      
      const targetPanel = this.querySelector(`[data-panel-id="${id}"] .tab-content-block`);
      if (!targetPanel) return;


      if (tabLabel) tabLabel.textContent = targetPanel.dataset.tabTitle;

    });
  }



  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handles tab click events.
   * @param {Event} event - The click event.
   */
  #handleTabClick = (event) => {
    event.preventDefault();
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLButtonElement)) return;

    const tabId = tab.dataset.tabId;
    if (tabId) {
      this.switchTab(tabId);
    }
  };

  /**
   * Handles keyboard navigation for tabs.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeydown = (event) => {
    const currentTab = event.currentTarget;
    if (!(currentTab instanceof HTMLButtonElement)) return;

    const tabsArray = this.tabs;
    const currentIndex = tabsArray.indexOf(currentTab);
    let targetIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = (currentIndex + 1) % tabsArray.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = (currentIndex - 1 + tabsArray.length) % tabsArray.length;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = tabsArray.length - 1;
        break;
      default:
        return;
    }

    tabsArray[targetIndex]?.focus();
    const targetTabId = tabsArray[targetIndex]?.dataset.tabId;
    if (targetTabId) {
      this.switchTab(targetTabId);
    }
  };

  /**
   * Switches to the specified tab.
   * @param {string} tabId - The ID of the tab to switch to.
   */
  switchTab(tabId) {
    const targetPanel = this.querySelector(`[data-panel-id="${tabId}"]`);
    if (!targetPanel) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tabId === tabId;
      tab.classList.toggle('tab-accordion__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.panelId === tabId;
      panel.classList.toggle('tab-accordion__panel--active', isActive);

      if (isActive) {
        panel.removeAttribute('hidden');
        panel.offsetHeight;
      } else {
        setTimeout(() => {
          if (!panel.classList.contains('tab-accordion__panel--active')) {
            panel.setAttribute('hidden', '');
          }
        }, 300);
      }
    });
  }
}

if (!customElements.get('tab-accordion')) {
  customElements.define('tab-accordion', TabAccordion);
}
